import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/site";

const SERVICES = [
  {
    id: "deep-cleaning-card",
    title: "Deep & Routine Cleaning",
    desc: "Post-construction, move-in/out and routine housekeeping for coastal villas, apartments and commercial spaces.",
    img: "https://images.pexels.com/photos/21243614/pexels-photo-21243614.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-7 md:row-span-2",
    tag: "Most booked",
  },
  {
    id: "window-cleaning-card",
    title: "Window & Glass Squeegee",
    desc: "Salt-spray removal, high-reach glass and frame restoration for oceanfront estates.",
    img: "https://images.pexels.com/photos/33615263/pexels-photo-33615263.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-5",
  },
  {
    id: "gardening-lawn-card",
    title: "Gardening & Lawn Mowing",
    desc: "Tropical garden care, palm trimming, precision lawn mowing, weed control and hedge sculpting.",
    img: "https://images.pexels.com/photos/3999647/pexels-photo-3999647.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-5",
  },
  {
    id: "painting-card",
    title: "Interior & Exterior Painting",
    desc: "Weather-proof exterior coatings against coastal dampness, interior repainting and trim touch-ups.",
    img: "https://images.pexels.com/photos/5691501/pexels-photo-5691501.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-6",
  },
  {
    id: "repairs-card",
    title: "Repairs & Odd Jobs",
    desc: "Plumbing tweaks, woodwork fixes, fixture mounting, pressure washing and general property maintenance.",
    img: "https://images.unsplash.com/photo-1635445818409-64a0ff92eb39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxjbGVhbmVyJTIwd2FzaGluZyUyMGdsYXNzJTIwd2luZG93JTIwcGFpbnRpbmclMjB3YWxsJTIwcmVwYWlyJTIwaGFuZHltYW58ZW58MHx8fHwxNzkyNzQyMzgxfDA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-6",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-14 max-w-2xl"
      >
        <p className="font-jbm text-xs font-semibold uppercase tracking-[0.3em] text-[#C85A32]">What we do</p>
        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#1C3B2B] sm:text-4xl lg:text-5xl">
          One crew. Every corner of your property.
        </h2>
      </motion.div>

      <div data-testid="services-bento-grid" className="grid grid-cols-1 gap-6 md:grid-cols-12 md:auto-rows-[270px] md:gap-7">
        {SERVICES.map((s, i) => (
          <motion.button
            key={s.id}
            data-testid={s.id}
            onClick={() => scrollToId("#quote")}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.75, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
            className={`group relative h-72 overflow-hidden rounded-3xl text-left md:h-full ${s.span}`}
          >
            <img
              src={s.img}
              alt={s.title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#132A1F]/90 via-[#132A1F]/25 to-transparent transition-opacity duration-500" />
            {s.tag && (
              <span className="absolute left-5 top-5 rounded-full bg-[#C85A32] px-4 py-1.5 font-jbm text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                {s.tag}
              </span>
            )}
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{s.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-[#FBF9F5]/80">{s.desc}</p>
                </div>
                <span className="mb-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white backdrop-blur-sm transition-[background-color,transform] duration-300 group-hover:bg-[#C85A32] group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}
